import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
  signal,
  forwardRef,
  effect,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { ClipboardService } from '@core/services/clipboard.service';
import { SvgIconComponent } from 'angular-svg-icon';

@Component({
  selector: 'app-form-field',
  imports: [FormsModule, SvgIconComponent],
  templateUrl: './form-field.component.html',
  styleUrl: './form-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormFieldComponent),
      multi: true,
    },
  ],
})
export class FormFieldComponent implements ControlValueAccessor {
  readonly label = input<string>('');
  readonly placeholder = input<string>('');
  readonly icon = input<string | null>(null);
  readonly disabled = input<boolean>(false);
  readonly error = input<string | boolean>(false);
  readonly errorType = input<'text-bottom' | 'outline'>('text-bottom');
  readonly value = model<string | number>();
  readonly id = input.required<string>();
  readonly type = input<'text' | 'number'>('text');
  readonly submitting = input<boolean>(false);
  readonly copy = input<{
    successMessage: string;
    iconColor?: string;
    iconOpacity?: number;
  } | null>(null);
  readonly maxlength = input<number | null>(null);
  readonly step = input<string | number>('any');
  readonly required = input<boolean>(false);

  readonly clipboardService = inject(ClipboardService);

  private readonly _disabledByForm = signal(false);
  readonly isDisabled = computed(() => this.disabled() || this._disabledByForm());

  private onChange: (value: string | number) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    effect(() => {
      const currentValue = this.value();
      if (currentValue !== undefined) {
        this.onChange(currentValue);
      }
    });
  }

  writeValue(value: string | number): void {
    if (value !== undefined) {
      this.value.set(value);
    }
  }

  registerOnChange(fn: (value: string | number) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabledByForm.set(isDisabled);
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.value.set(input.value);
    this.onChange(input.value);
    this.onTouched();
  }
}
